import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import {
  CardStatus,
  ConfigHistoryEntry,
  ConfigLocation,
  DeptMetricsMap,
  FocusLists,
  FocusOverrides,
  OrgDeptPair,
  ScopedSection,
  ScopedValues,
  TargetsMap,
} from '../../screens/PeopleHub/PeopleHubConfiguration/configTypes';
import type { LocationOnboardingConfig } from '../../screens/PeopleHub/PeopleHubConfiguration/types';

// ─── Types ────────────────────────────────────────────────────────────────────

export type NewConfigHistoryEntry = Omit<ConfigHistoryEntry, 'at' | 'by'>;

/**
 * People Hub Configuration state.
 *
 * On first render the slice holds empty/loading values; `PeopleConfigContext`
 * populates it from the backend once the user navigates to the page.
 *
 * Reads in the UI always come from Redux; saves call the API first and only
 * update Redux on a successful response.
 */
export interface PeopleConfigState {
  org: string;
  dept: string;
  /** Available org+department pairs. Populated from GET /metadata. */
  orgDepts: OrgDeptPair[];
  /** Locations from Admin Location Management (org + departments). Populated from GET /metadata. */
  locations: ConfigLocation[];
  /** Metrics each org and department uses (keyed `<org>:<dept>`). */
  deptMetrics: DeptMetricsMap;
  cadence: ScopedValues;
  escalation: ScopedValues;
  targets: TargetsMap;
  /** Saved mobile roster card metrics, and the unsaved draft being edited, keyed `<org>:<dept>`. */
  rosters: Record<string, string[]>;
  rosterDrafts: Record<string, string[]>;
  focusLists: FocusLists;
  focusOverrides: FocusOverrides;
  /** Newest first. */
  history: ConfigHistoryEntry[];
  /**
   * Server-confirmed Onboarding Ramp configs, keyed `national:<org>:<dept>` or `location:<location>:<dept>`.
   * Filled on demand by the Ramp tab; never seeded with defaults.
   */
  ramp: Record<string, LocationOnboardingConfig>;
  status: CardStatus;
  conflict: boolean;
  /** Message from the last failed save (shown by TabBody); cleared when the next save starts. */
  saveError: string | null;
}

const initialState: PeopleConfigState = {
  org: 'PBNA',
  dept: 'Warehouse',
  orgDepts: [],
  locations: [],
  deptMetrics: {},
  cadence: {},
  escalation: {},
  targets: {},
  rosters: {},
  rosterDrafts: {},
  focusLists: {},
  focusOverrides: {},
  history: [],
  ramp: {},
  status: 'loading',
  conflict: false,
  saveError: null,
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const peopleConfigSlice = createSlice({
  name: 'peopleConfig',
  initialState,
  reducers: {
    setScope(state, action: PayloadAction<{ org: string; dept: string }>) {
      state.conflict = false;
      state.saveError = null;
      state.org = action.payload.org;
      state.dept = action.payload.dept;
    },
    /** Populated once from GET /metadata (or the embedded metadata in GET /?org=&department=). */
    setOrgDepts(state, action: PayloadAction<OrgDeptPair[]>) {
      state.orgDepts = action.payload;
    },
    /** Populated once from GET /metadata (or the embedded metadata in GET /?org=&department=). */
    setLocations(state, action: PayloadAction<ConfigLocation[]>) {
      state.locations = action.payload;
    },
    setDeptMetrics(state, action: PayloadAction<DeptMetricsMap>) {
      state.deptMetrics = action.payload;
    },
    setScopedValues(state, action: PayloadAction<{ section: ScopedSection; values: ScopedValues }>) {
      state[action.payload.section] = action.payload.values;
    },
    setTargets(state, action: PayloadAction<TargetsMap>) {
      state.targets = action.payload;
    },
    setRosters(state, action: PayloadAction<Record<string, string[]>>) {
      state.rosters = action.payload;
    },
    setRosterDrafts(state, action: PayloadAction<Record<string, string[]>>) {
      state.rosterDrafts = action.payload;
    },
    setFocusLists(state, action: PayloadAction<FocusLists>) {
      state.focusLists = action.payload;
    },
    setFocusOverrides(state, action: PayloadAction<FocusOverrides>) {
      state.focusOverrides = action.payload;
    },
    /** Replaces the full history list (used when loading from API). */
    setHistory(state, action: PayloadAction<ConfigHistoryEntry[]>) {
      state.history = action.payload;
    },
    /** Writes who/when/setting/prior/new/effective to Change History, newest first. */
    appendHistory: {
      reducer(state, action: PayloadAction<ConfigHistoryEntry[]>) {
        state.history = [...action.payload, ...state.history];
      },
      // Timestamp is stamped here so the reducer stays pure and each dispatch adds each entry once.
      prepare(entries: NewConfigHistoryEntry[], by: string) {
        const at = new Date().toISOString();
        return { payload: entries.map((e) => ({ at, by, ...e })) };
      },
    },
    setRampConfig(state, action: PayloadAction<{ key: string; config: LocationOnboardingConfig }>) {
      state.ramp[action.payload.key] = action.payload.config;
    },
    clearRampConfig(state, action: PayloadAction<string>) {
      delete state.ramp[action.payload];
    },
    /** After a national ramp save, locations that inherit it must be fetched again. */
    clearInheritedRamps(state) {
      Object.keys(state.ramp).forEach((k) => {
        if (state.ramp[k].scopeType === 'INHERITED') delete state.ramp[k];
      });
    },
    setSaveError(state, action: PayloadAction<string | null>) {
      state.saveError = action.payload;
    },
    setConfigStatus(state, action: PayloadAction<CardStatus>) {
      state.status = action.payload;
    },
    setConfigConflict(state, action: PayloadAction<boolean>) {
      state.conflict = action.payload;
    },
  },
});

export const {
  setScope,
  setOrgDepts,
  setLocations,
  setDeptMetrics,
  setScopedValues,
  setTargets,
  setRosters,
  setRosterDrafts,
  setFocusLists,
  setFocusOverrides,
  setHistory,
  appendHistory,
  setRampConfig,
  clearRampConfig,
  clearInheritedRamps,
  setSaveError,
  setConfigStatus,
  setConfigConflict,
} = peopleConfigSlice.actions;

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectPeopleConfigOrg = (state: RootState) => state.peopleConfig.org;
export const selectPeopleConfigDept = (state: RootState) => state.peopleConfig.dept;
export const selectPeopleConfigOrgDepts = (state: RootState) => state.peopleConfig.orgDepts;
export const selectPeopleConfigLocations = (state: RootState) => state.peopleConfig.locations;
export const selectDeptMetrics = (state: RootState) => state.peopleConfig.deptMetrics;
export const selectScopedValues = (state: RootState, section: ScopedSection) =>
  state.peopleConfig[section];
export const selectTargets = (state: RootState) => state.peopleConfig.targets;
export const selectRosters = (state: RootState) => state.peopleConfig.rosters;
export const selectRosterDrafts = (state: RootState) => state.peopleConfig.rosterDrafts;
export const selectFocusLists = (state: RootState) => state.peopleConfig.focusLists;
export const selectFocusOverrides = (state: RootState) => state.peopleConfig.focusOverrides;
export const selectConfigHistory = (state: RootState) => state.peopleConfig.history;
export const selectRamp = (state: RootState) => state.peopleConfig.ramp;
export const selectSaveError = (state: RootState) => state.peopleConfig.saveError;
export const selectConfigStatus = (state: RootState) => state.peopleConfig.status;
export const selectConfigConflict = (state: RootState) => state.peopleConfig.conflict;

export default peopleConfigSlice.reducer;
